import React, { useState, useEffect } from "react";
import {
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
  makeStyles,
  CircularProgress
} from "@material-ui/core";
import { format, parseISO } from "date-fns";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  title: {
    marginBottom: theme.spacing(2),
    fontWeight: 500,
  },
  listItem: {
    flexDirection: "column",
    alignItems: "flex-start",
  },
  typeChip: {
    marginRight: theme.spacing(1),
    fontWeight: 500,
  },
  categoryChip: {
    marginRight: theme.spacing(1),
  },
  observation: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1),
    backgroundColor: theme.palette.action.hover,
    borderRadius: theme.shape.borderRadius,
    fontStyle: "italic",
  },
  metadata: {
    marginTop: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
  },
  loading: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(3),
  },
  noData: {
    textAlign: "center",
    padding: theme.spacing(3),
    color: theme.palette.text.secondary,
  },
}));

const TicketClassificationHistory = ({ ticketId }) => {
  const classes = useStyles();
  const [classifications, setClassifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (ticketId) {
      fetchClassifications();
    }
  }, [ticketId]);

  const fetchClassifications = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/ticket-classifications", {
        params: { ticketId },
      });
      setClassifications(data);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Paper className={classes.paper} variant="outlined">
        <div className={classes.loading}>
          <CircularProgress size={24} />
        </div>
      </Paper>
    );
  }

  if (classifications.length === 0) {
    return (
      <Paper className={classes.paper} variant="outlined">
        <Typography variant="h6" className={classes.title}>
          {i18n.t("ticketClassificationHistory.title")}
        </Typography>
        <div className={classes.noData}>
          <Typography variant="body2">
            {i18n.t("ticketClassificationHistory.noClassifications")}
          </Typography>
        </div>
      </Paper>
    );
  }

  return (
    <Paper className={classes.paper} variant="outlined">
      <Typography variant="h6" className={classes.title}>
        {i18n.t("ticketClassificationHistory.title")}
      </Typography>
      <List>
        {classifications.map((classification, index) => (
          <React.Fragment key={classification.id}>
            {index > 0 && <Divider />}
            <ListItem className={classes.listItem}>
              <div>
                <Chip
                  size="small"
                  label={classification.type.name}
                  className={classes.typeChip}
                  style={{
                    backgroundColor: classification.type.color,
                    color: "#fff",
                  }}
                />
                <Chip
                  size="small"
                  label={classification.category.name}
                  className={classes.categoryChip}
                  variant="outlined"
                />
              </div>
              {classification.observation && (
                <div className={classes.observation}>
                  <Typography variant="body2">
                    {classification.observation}
                  </Typography>
                </div>
              )}
              <div className={classes.metadata}>
                <Typography variant="caption">
                  {`Classificado por ${classification.user.name} em ${format(
                    parseISO(classification.createdAt),
                    "dd/MM/yyyy HH:mm"
                  )}`}
                </Typography>
              </div>
            </ListItem>
          </React.Fragment>
        ))}
      </List>
    </Paper>
  );
};

export default TicketClassificationHistory;
